import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import {alpha} from '@mui/material/styles';
import React from 'react';

/** The strip's height (px). The canvas's top panels move down by exactly this while one shows. */
export const CANVAS_STRIP_HEIGHT = 44;

export type CanvasStripTone = 'info' | 'success' | 'warning' | 'error';

interface CanvasStripProps {
    tone: CanvasStripTone;
    icon: React.ReactNode;
    message: React.ReactNode;
    /** The whole sentence as text, for the tooltip when a narrow canvas cuts it short. */
    title?: string;
    actions?: React.ReactNode;
    busy?: boolean;
}

/**
 * A mode's strip, laid over the canvas so nothing moves; only the top panels step down by CANVAS_STRIP_HEIGHT.
 * Preview is blue (the navy primary tints to grey), a test run orange, either red on failure.
 * Not closable on its own: leaving the mode removes it.
 */
const CanvasStrip = ({tone, icon, message, title, actions, busy}: CanvasStripProps) => (
    <Box
        role={'status'}
        aria-live={'polite'}
        sx={theme => {
            const color = theme.palette[tone].main;
            return {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                // Above the canvas panels (React Flow draws them at z-index 5).
                zIndex: 6,
                height: CANVAS_STRIP_HEIGHT,
                boxSizing: 'border-box',
                pl: 2,
                pr: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                overflow: 'hidden',
                color: tone === 'error' ? 'error.main' : `${tone}.dark`,
                borderBottom: '1px solid',
                borderColor: alpha(color, 0.3),
                // Opaque, so the dotted canvas never shows through the tint.
                background: `linear-gradient(${alpha(color, 0.1)}, ${alpha(color, 0.1)}), ${theme.palette.background.paper}`,
            };
        }}
    >
        <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0, '& > svg': {fontSize: 20}}}>
            {icon}
        </Box>

        <Typography variant={'body1'} color={'inherit'} noWrap title={title} sx={{flex: '0 1 auto', minWidth: 0}}>
            {message}
        </Typography>

        {actions}

        {busy && (
            <LinearProgress
                aria-hidden
                color={tone}
                sx={{position: 'absolute', left: 0, right: 0, bottom: 0, height: 2}}
            />
        )}
    </Box>
);

export default CanvasStrip;
